import React, { startTransition, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Box,
  useToast,
  VStack,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Badge,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Textarea,
  Input,
} from '@chakra-ui/react'
import { FiLink2, FiEdit2 } from 'react-icons/fi'
import FolderTree from '@/components/FolderTree'
import { useNavigation } from '@/hooks/useNavigation'
import { FileItem, Folder, FileLinksResponse, FileLinkRow, FileLinkRelationType } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAppDispatch } from '@/store'
import { fetchFolderFiles } from '@/store/slices/filesSlice'
import { FILE_LINK_RELATION_TYPES } from '@/constants/fileLinks'

type Step = 'list' | 'pick'

const NOTE_MAX_LENGTH = 500

interface LinkDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  sourceFileId: number
  onLinked: () => void
}

interface RelationTypeSelectProps {
  value: FileLinkRelationType
  onChange: (value: FileLinkRelationType) => void
  size?: 'sm' | 'md'
  isDisabled?: boolean
}

function RelationTypeSelect({ value, onChange, size = 'md', isDisabled }: RelationTypeSelectProps) {
  const { t } = useAppTranslation('files')
  return (
    <Select
      size={size}
      value={value}
      isDisabled={isDisabled}
      onChange={(e) => onChange(e.target.value as FileLinkRelationType)}
    >
      {FILE_LINK_RELATION_TYPES.map((type) => (
        <option key={type} value={type}>
          {t(`filePreview.linkTypes.${type}`)}
        </option>
      ))}
    </Select>
  )
}

interface LinkRowProps {
  row: FileLinkRow
  direction: 'outgoing' | 'incoming'
  sourceFileId: number
  busyLinkId: number | null
  onOpen: (fileId: number) => void
  onUnlink: (linkId: number) => Promise<void>
  onUpdated: () => Promise<void>
}

function LinkRow({ row, direction, sourceFileId, busyLinkId, onOpen, onUnlink, onUpdated }: LinkRowProps) {
  const { t } = useAppTranslation(['files', 'common'])
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [relationType, setRelationType] = useState<FileLinkRelationType>(row.relation_type)
  const [note, setNote] = useState(row.note ?? '')
  const [saving, setSaving] = useState(false)

  const otherBusy = busyLinkId !== null && busyLinkId !== row.id
  const typeLabel =
    direction === 'outgoing'
      ? t(`filePreview.linkTypes.${row.relation_type}`)
      : t(`filePreview.linkTypes.inverse.${row.relation_type}`)

  const startEdit = () => {
    setRelationType(row.relation_type)
    setNote(row.note ?? '')
    setEditing(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await apiService.updateFileLink(sourceFileId, row.id, {
        relation_type: relationType,
        note: note.trim() || null,
      })
      toast({
        title: t('filePreview.linkDocuments.updateSuccess'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      })
      setEditing(false)
      await onUpdated()
    } catch {
      toast({
        title: t('filePreview.linkDocuments.updateError'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box w="full" p={2} borderRadius="md" borderWidth="1px" borderColor="gray.200" bg="gray.50">
      <HStack w="full" align="center" justify="space-between" spacing={3}>
        <Box flex={1} minW={0}>
          <Button
            variant="link"
            size="sm"
            colorScheme="brand"
            onClick={() => onOpen(row.file.id)}
            noOfLines={2}
            justifyContent="flex-start"
            textAlign="left"
            whiteSpace="normal"
            h="auto"
            py={1}
            px={0}
          >
            {row.file.display_name || row.file.original_name}
          </Button>
          {!editing && (
            <HStack spacing={2} mt={1} align="center">
              <Badge colorScheme="brand" variant="subtle" fontSize="0.65rem">
                {typeLabel}
              </Badge>
              {row.note && (
                <Text fontSize="xs" color="gray.600" noOfLines={1} textAlign="left">
                  {row.note}
                </Text>
              )}
            </HStack>
          )}
        </Box>
        <HStack flexShrink={0} spacing={1}>
          <IconButton
            aria-label={t('filePreview.linkDocuments.editLink')}
            icon={<FiEdit2 />}
            size="xs"
            variant="ghost"
            isDisabled={otherBusy || saving}
            onClick={editing ? () => setEditing(false) : startEdit}
          />
          <Button
            size="xs"
            variant="outline"
            colorScheme="red"
            isLoading={busyLinkId === row.id}
            isDisabled={otherBusy || saving}
            onClick={() => void onUnlink(row.id)}
          >
            {t('filePreview.linkDocuments.removeLink')}
          </Button>
        </HStack>
      </HStack>
      {editing && (
        <VStack align="stretch" spacing={2} mt={2}>
          <RelationTypeSelect value={relationType} onChange={setRelationType} size="sm" isDisabled={saving} />
          <Input
            size="sm"
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            placeholder={t('filePreview.linkDocuments.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
            isDisabled={saving}
          />
          <HStack justify="flex-end" spacing={2}>
            <Button size="xs" variant="ghost" onClick={() => setEditing(false)} isDisabled={saving}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button size="xs" colorScheme="brand" onClick={() => void handleSave()} isLoading={saving}>
              {t('filePreview.linkDocuments.saveLink')}
            </Button>
          </HStack>
        </VStack>
      )}
    </Box>
  )
}

export default function LinkDocumentModal({
  isOpen,
  onClose,
  sourceFileId,
  onLinked,
}: LinkDocumentModalProps) {
  const { folderTree } = useNavigation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useAppTranslation(['files', 'common'])
  const toast = useToast()

  const [step, setStep] = useState<Step>('list')
  const [links, setLinks] = useState<FileLinksResponse>({ outgoing: [], incoming: [] })
  const [linksLoading, setLinksLoading] = useState(false)
  const [selected, setSelected] = useState<FileItem | null>(null)
  const [relationType, setRelationType] = useState<FileLinkRelationType>('related')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null)

  const loadLinks = useCallback(async () => {
    setLinksLoading(true)
    try {
      const data = await apiService.getFileLinks(sourceFileId)
      setLinks(data)
    } catch {
      setLinks({ outgoing: [], incoming: [] })
    } finally {
      setLinksLoading(false)
    }
  }, [sourceFileId])

  const resetPick = () => {
    setSelected(null)
    setRelationType('related')
    setNote('')
  }

  useEffect(() => {
    setStep('list')
    setSelected(null)
    setRelationType('related')
    setNote('')
    if (!isOpen) {
      return
    }
    void loadLinks()
    // Warm Redux with root files so “All files” / root picks are faster when user adds a link.
    void dispatch(fetchFolderFiles(null))
  }, [isOpen, sourceFileId, loadLinks, dispatch])

  const handleFolderSelect = useCallback(
    (folder: Folder | null) => {
      void dispatch(fetchFolderFiles(folder?.id ?? null))
    },
    [dispatch]
  )

  const goPreview = (id: number) => {
    const qs = searchParams.toString()
    navigate(qs ? `/preview/${id}?${qs}` : `/preview/${id}`)
    onClose()
  }

  const handleUnlink = async (linkId: number) => {
    setUnlinkingId(linkId)
    try {
      await apiService.deleteFileLink(sourceFileId, linkId)
      toast({
        title: t('filePreview.linkDocuments.unlinkSuccess'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      })
      await loadLinks()
      onLinked()
    } catch {
      toast({
        title: t('filePreview.linkDocuments.unlinkError'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setUnlinkingId(null)
    }
  }

  const handleLinkUpdated = async () => {
    await loadLinks()
    onLinked()
  }

  const handleConfirmPick = async () => {
    if (!selected) return
    if (selected.id === sourceFileId) {
      toast({
        title: t('filePreview.linkDocuments.selfError'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setSaving(true)
    try {
      await apiService.createFileLink(sourceFileId, selected.id, {
        relation_type: relationType,
        note: note.trim() || null,
      })
      toast({
        title: t('filePreview.linkDocuments.success'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      onLinked()
      resetPick()
      setStep('list')
      await loadLinks()
    } catch {
      toast({
        title: t('filePreview.linkDocuments.error'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const onFileSelect = (file: FileItem) => {
    if (file.id === sourceFileId) {
      toast({
        title: t('filePreview.linkDocuments.selfError'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setSelected(file)
  }

  const hasAnyLinks = links.outgoing.length > 0 || links.incoming.length > 0

  const renderRows = (rows: FileLinkRow[], direction: 'outgoing' | 'incoming') => (
    <VStack align="stretch" spacing={2}>
      {rows.map((row) => (
        <LinkRow
          key={row.id}
          row={row}
          direction={direction}
          sourceFileId={sourceFileId}
          busyLinkId={unlinkingId}
          onOpen={goPreview}
          onUnlink={handleUnlink}
          onUpdated={handleLinkUpdated}
        />
      ))}
    </VStack>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" maxH="90vh">
        <ModalHeader textAlign="left">
          {step === 'list' ? t('filePreview.linkDocuments.modalTitle') : t('filePreview.linkDocuments.pickTitle')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          overflowY="auto"
          maxH={step === 'list' ? '65vh' : '60vh'}
          textAlign="left"
        >
          {step === 'list' && (
            <>
              {linksLoading && (
                <HStack py={8} justify="center" spacing={3}>
                  <Spinner color="brand.500" />
                  <Text color="gray.600">{t('filePreview.linkDocuments.loadingLinks')}</Text>
                </HStack>
              )}
              {!linksLoading && !hasAnyLinks && (
                <Text fontSize="sm" color="gray.600" mb={4}>
                  {t('filePreview.linkDocuments.listEmpty')}
                </Text>
              )}
              {!linksLoading && hasAnyLinks && (
                <VStack align="stretch" spacing={4} pb={2}>
                  {links.outgoing.length > 0 && (
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mb={2}>
                        {t('filePreview.linkDocuments.outgoing')}
                      </Text>
                      {renderRows(links.outgoing, 'outgoing')}
                    </Box>
                  )}
                  {links.incoming.length > 0 && (
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mb={2}>
                        {t('filePreview.linkDocuments.incoming')}
                      </Text>
                      {renderRows(links.incoming, 'incoming')}
                    </Box>
                  )}
                </VStack>
              )}
            </>
          )}

          {step === 'pick' && (
            <>
              <Text fontSize="sm" color="gray.600" mb={2}>
                {t('filePreview.linkDocuments.instructions')}
              </Text>
              {selected && (
                <Text fontSize="sm" fontWeight="600" mb={2} color="gray.800">
                  {t('filePreview.linkDocuments.selected')}: {selected.display_name || selected.original_name}
                </Text>
              )}
              <Box maxH="40vh" overflowY="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={1}>
                <FolderTree
                  folders={folderTree}
                  selectedFolder={null}
                  onFolderSelect={handleFolderSelect}
                  onFileSelect={onFileSelect}
                  pickerMode
                />
              </Box>
              <HStack align="flex-start" spacing={3} mt={3}>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm" mb={1}>
                    {t('filePreview.linkDocuments.relationType')}
                  </FormLabel>
                  <RelationTypeSelect value={relationType} onChange={setRelationType} size="sm" isDisabled={saving} />
                  <FormHelperText fontSize="xs">{t('filePreview.linkDocuments.relationTypeHelp')}</FormHelperText>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm" mb={1}>
                    {t('filePreview.linkDocuments.note')}
                  </FormLabel>
                  <Textarea
                    size="sm"
                    rows={2}
                    resize="none"
                    value={note}
                    maxLength={NOTE_MAX_LENGTH}
                    placeholder={t('filePreview.linkDocuments.notePlaceholder')}
                    onChange={(e) => setNote(e.target.value)}
                    isDisabled={saving}
                  />
                </FormControl>
              </HStack>
            </>
          )}
        </ModalBody>

        <ModalFooter flexWrap="wrap" gap={2} justifyContent="flex-start">
          {step === 'list' && (
            <>
              <Button variant="ghost" onClick={onClose}>
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button
                colorScheme="brand"
                leftIcon={<Icon as={FiLink2} />}
                onClick={() => {
                  resetPick()
                  startTransition(() => {
                    setStep('pick')
                  })
                }}
              >
                {t('filePreview.linkDocuments.addLink')}
              </Button>
            </>
          )}
          {step === 'pick' && (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  resetPick()
                  setStep('list')
                }}
              >
                {t('filePreview.linkDocuments.backToList')}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button
                colorScheme="brand"
                onClick={handleConfirmPick}
                isLoading={saving}
                isDisabled={!selected}
              >
                {t('filePreview.linkDocuments.confirm')}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
